import { useState } from 'react'
import { redirectToImage } from '@/utils/redirect'
import { getRandomPrompt } from '@/config/example-prompts'

const EXAMPLE_VIDEOS = [
  'https://funnyclips-content.s3.us-east-1.amazonaws.com/example14.mp4',
  'https://funnyclips-content.s3.us-east-1.amazonaws.com/example15.mp4',
  'https://funnyclips-content.s3.us-east-1.amazonaws.com/example_4.mp4',
  'https://funnyclips-content.s3.us-east-1.amazonaws.com/example_8.mp4',
]

export function SqueezeSelectorPage() {
  const [prompt, setPrompt] = useState('')

  const handleCreate = () => {
    if (!prompt.trim()) return
    redirectToImage('t2i', { prompt: prompt.trim() })
  }

  const handleGiveMeAnIdea = () => {
    setPrompt(getRandomPrompt(prompt))
  }

  return (
    <div className="h-dvh overflow-y-auto bg-black text-white">
      {/* Hero - centered single column */}
      <div className="relative min-h-screen flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900 opacity-50" />

        <div className="relative z-10 max-w-2xl mx-auto w-full text-center">
          {/* Badge */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/20 rounded-full px-4 py-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span className="text-white text-sm font-semibold">
                No Limits. No Rules. No Filter.
              </span>
            </div>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black leading-tight mb-6">
            Describe Her.
            <br />
            Create Her.
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-10">
            Type exactly what you want and we'll bring her to life
          </p>

          {/* Prompt Input */}
          <div className="space-y-5">
            <div className="relative">
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe your dream girl... e.g. a beautiful brunette with green eyes, wearing a red dress, dancing on the beach at sunset"
                rows={5}
                className="w-full bg-white/5 border border-white/20 rounded-2xl px-5 py-4 pb-14 text-white placeholder-gray-500 text-lg resize-none focus:outline-none focus:border-white/40 focus:ring-1 focus:ring-white/20 transition-all text-left"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handleCreate()
                  }
                }}
              />
              <button
                type="button"
                onClick={handleGiveMeAnIdea}
                className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 text-white text-sm font-semibold px-3 py-1.5 rounded-full transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Give me an idea
              </button>
            </div>

            <button
              onClick={handleCreate}
              disabled={!prompt.trim()}
              className="w-full inline-flex items-center justify-center gap-3 bg-white hover:bg-gray-200 text-black text-xl md:text-2xl font-bold px-10 py-5 md:px-12 md:py-6 rounded-full transition-all duration-300 shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:shadow-[0_0_50px_rgba(255,255,255,0.5)] hover:scale-105 disabled:opacity-40 disabled:hover:scale-100 disabled:hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] disabled:cursor-not-allowed"
            >
              Create Her Now
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Example Videos */}
      <div className="relative px-4 py-16 bg-gradient-to-b from-black via-gray-900 to-black">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
              See What Others Created
            </h2>
            <p className="text-gray-400 text-lg">
              Real examples from real creators
            </p>
          </div>

          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory justify-center">
            {EXAMPLE_VIDEOS.map((src) => (
              <div
                key={src}
                className="group relative flex-shrink-0 w-48 md:w-56 aspect-[3/4] rounded-xl overflow-hidden border border-white/20 hover:border-white/40 transition-all duration-300 snap-start"
              >
                <video
                  src={src}
                  loop
                  muted
                  playsInline
                  autoPlay
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="relative px-4 py-8 bg-black border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm leading-relaxed">
            The "No Filter" mode is designed for creative and entertainment
            purposes. Users are responsible for ensuring their content complies
            with platform guidelines and applicable laws. PixelMaker does not
            endorse or take responsibility for user-generated content.
          </p>
        </div>
      </div>
    </div>
  )
}
